<template>
  <div>
    <PageHeader :title="isEdit ? 'Edit User' : 'Add User'" />

    <div class="card p-4">
      <b-form @submit.prevent="saveUser">
        <b-row>
          <b-col md="6">
            <b-form-group label="First Name" label-for="first_name">
              <b-form-input id="first_name" v-model="form.first_name" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Last Name" label-for="last_name">
              <b-form-input id="last_name" v-model="form.last_name" required />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Email" label-for="email">
              <b-form-input id="email" v-model="form.email" type="email" required :disabled="isEdit" />
            </b-form-group>
          </b-col>
          <b-col md="6" v-if="!isEdit">
            <b-form-group label="Password" label-for="password">
              <b-form-input id="password" v-model="form.password" type="password" required />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Role" label-for="role">
              <b-form-select id="role" v-model="form.role" :options="roleOptions" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="School" label-for="school">
              <b-form-select id="school" v-model="form.school" :options="schoolOptions">
                <template #first>
                  <b-form-select-option :value="null">-- No School --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Status">
              <b-form-checkbox v-model="form.is_active" switch>Active</b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="outline-secondary" class="mr-2" :to="{ name: 'manage-users' }">Cancel</b-button>
          <b-button type="submit" variant="primary" :disabled="isSaving">
            <b-spinner v-if="isSaving" small class="mr-1" />{{ isEdit ? 'Update' : 'Create' }}
          </b-button>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '../../components/Ui/PageHeader.vue';

export default {
  name: 'PostUser',
  components: { PageHeader },
  data() {
    return {
      isSaving: false,
      schools: [],
      form: this.getEmptyForm(),
      roleOptions: [
        { value: 'super_admin', text: 'Super Admin' },
        { value: 'school_owner', text: 'School Owner' },
        { value: 'school_admin', text: 'School Admin' },
      ],
    };
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },
    schoolOptions() {
      return this.schools.map(s => ({ value: s.id, text: s.name }));
    },
  },
  methods: {
    ...mapActions('user', ['getUser', 'createUser', 'updateUser']),
    ...mapActions('school', ['getAllSchools']),
    getEmptyForm() {
      return {
        first_name: '', last_name: '', email: '', password: '',
        role: 'school_admin', school: null, is_active: true,
      };
    },
    async loadUser() {
      if (!this.isEdit) return;
      try {
        const res = await this.getUser(this.$route.params.id);
        const u = res.data || res;
        this.form.first_name = u.first_name || '';
        this.form.last_name = u.last_name || '';
        this.form.email = u.email || '';
        this.form.role = u.role || 'school_admin';
        this.form.school = u.school ? u.school.id : null;
        this.form.is_active = u.is_active !== false;
      } catch (err) {
        this.$bvToast && this.$bvToast.toast('Failed to load user', { variant: 'danger', solid: true });
      }
    },
    async loadSchools() {
      try {
        const res = await this.getAllSchools({ limit: 1000 });
        this.schools = res.data.results || res.data || [];
      } catch (err) {
        // silent
      }
    },
    async saveUser() {
      this.isSaving = true;
      try {
        const payload = { ...this.form };
        if (this.isEdit) {
          delete payload.password;
          delete payload.email;
          await this.updateUser({ id: this.$route.params.id, data: payload });
          this.$bvToast && this.$bvToast.toast('User updated', { variant: 'success', solid: true });
        } else {
          await this.createUser(payload);
          this.$bvToast && this.$bvToast.toast('User created', { variant: 'success', solid: true });
          this.$router.push({ name: 'manage-users' });
        }
      } catch (err) {
        const msg = err.response?.data?.detail || err.response?.data?.message || 'Save failed';
        this.$bvToast && this.$bvToast.toast(msg, { variant: 'danger', solid: true });
      } finally {
        this.isSaving = false;
      }
    },
  },
  created() {
    this.loadSchools();
    this.loadUser();
  },
};
</script>
